import { FC, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
	Box,
	Text,
	Button,
	Flex,
	HStack,
	Avatar,
} from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import UserContext from 'src/contexts/user/UserContext';
import CreativeCard from '../CreativeCard';
import MiniGlobeIcon from 'src/assets/icons/MiniGlobe';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import { formatCallToAction } from '../utils/formatedCTA';
import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview, { TemplatePreviewHandle } from 'src/components/templates/TemplatePreview';
import { IoMdLink } from 'react-icons/io';
import { FaPlay, FaPause, FaExpand } from 'react-icons/fa';
import VideoExpandModal from '../utils/VideoExpandModal';
import useSmoothProgress from '../utils/useSmoothProgress';

interface VideoStoryCreativeProps {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	isPaid?: boolean;
}

const VideoStoryCreative: FC<VideoStoryCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	isPaid,
}) => {
	const { account } = useContext(UserContext);
	const { callToAction } = (creative as any).attributes;
	const [logo, setLogo] = useState<string | undefined>(account?.logoSquare);
	const previewRef = useRef<TemplatePreviewHandle>(null);
	const [previewReady, setPreviewReady] = useState(false);
	const [hasStarted, setHasStarted] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const [duration, setDuration] = useState(10);
	const seekBarRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const { progressBarRef, timeDisplayRef, onTimeChange, setTimeImmediate } = useSmoothProgress({ duration, isPlaying });

	const onPreviewReady = useCallback(() => {
		setPreviewReady(true);
		setTimeout(() => {
			if (previewRef.current) {
				previewRef.current.setLoop(false);
				previewRef.current.setControls(false);
				previewRef.current.setTime(5);
				setDuration(previewRef.current.getDuration() || 10);
			}
		}, 300);
	}, []);

	const isPending =
		creative.status === 'pending' || creative.status === 'generating';

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);

	const videoTemplate = useMemo(() => {
		if (isCreatomate) {
			return JSON.parse(JSON.stringify(creative.attributes));
		}
		return null;
	}, [JSON.stringify((creative as any).attributes.elements)]);

	const handlePlayPause = useCallback(() => {
		if (!previewRef.current) return;
		if (isPlaying) {
			previewRef.current.pause();
		} else {
			previewRef.current.play();
		}
	}, [isPlaying]);

	const handleInitialPlay = useCallback(() => {
		if (!previewRef.current) return;
		setHasStarted(true);
		previewRef.current.setTime(0);
		previewRef.current.play();
	}, []);

	const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!seekBarRef.current || !previewRef.current) return;
		const rect = seekBarRef.current.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		const newTime = ratio * duration;
		previewRef.current.setTime(newTime);
		setTimeImmediate(newTime);
	}, [duration, setTimeImmediate]);

	const onPlay = useCallback(() => setIsPlaying(true), []);
	const onPause = useCallback(() => setIsPlaying(false), []);

	useEffect(() => {
		setLogo(account?.logoSquare);
	}, [account]);

	return (
		<>
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size={size}
			title="Video Story"
			creativesConfig={creativesConfig}
		>
			{isPending ? (
				<Flex width="100%" h={{ base: '495px', md: '680px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<Box
					h={{ base: '495px', md: '680px' }}
					w="100%"
					position="relative"
					onMouseEnter={() => setIsHovering(true)}
					onMouseLeave={() => setIsHovering(false)}
				>
					<Box p={4} position="absolute" zIndex={3} pointerEvents="none">
						<Flex alignItems="center" mb={4} gap={2}>
							<Avatar
								key={logo || 'no-logo'}
								src={logo}
								sx={{ img: { objectFit: 'contain' } }}
							/>
							<Box>
								<Text color="white" fontWeight="bold" textShadow="0 1px 3px rgba(0,0,0,0.6)">
									{account?.name}
								</Text>
								<HStack spacing={1}>
									{isPaid && (
										<>
											<Text color="white" fontSize="sm" textShadow="0 1px 3px rgba(0,0,0,0.6)">
												Sponsored ·
											</Text>
											<MiniGlobeIcon />
										</>
									)}
								</HStack>
							</Box>
						</Flex>
					</Box>
					{isCreatomate && videoTemplate ? (
						<>
							<Box filter={previewReady && !hasStarted ? 'brightness(0.7)' : undefined} borderRadius="lg" overflow="hidden" h="100%">
								<TemplatePreview
									ref={previewRef}
									key={creative.id}
									template={videoTemplate}
									borderRadius="lg"
									onReady={onPreviewReady}
									onTimeChange={onTimeChange}
									onPlay={onPlay}
									onPause={onPause}
								/>
							</Box>
							{/* Initial centered play button */}
							{previewReady && !hasStarted && (
								<Flex
									position="absolute"
									top={0}
									left={0}
									right={0}
									bottom={0}
									align="center"
									justify="center"
									zIndex={4}
									cursor="pointer"
									onClick={handleInitialPlay}
								>
									<Flex
										w={{ base: '56px', md: '72px' }}
										h={{ base: '56px', md: '72px' }}
										borderRadius="full"
										bg="blackAlpha.600"
										align="center"
										justify="center"
										boxShadow="lg"
									>
										<Box as={FaPlay} color="white" fontSize={{ base: '20px', md: '28px' }} ml="4px" />
									</Flex>
								</Flex>
							)}
							{/* Video controls bar - on hover after started */}
							{hasStarted && isHovering && (
								<Flex
									position="absolute"
									bottom={0}
									left={0}
									right={0}
									bg="blackAlpha.700"
									px={3}
									py={2}
									align="center"
									gap={2}
									zIndex={5}
									borderBottomRadius="lg"
								>
									<Box
										as="button"
										onClick={handlePlayPause}
										color="white"
										cursor="pointer"
										flexShrink={0}
										_hover={{ opacity: 0.8 }}
									>
										<Box as={isPlaying ? FaPause : FaPlay} fontSize="14px" ml={isPlaying ? '0' : '2px'} />
									</Box>
									<Box
										ref={seekBarRef}
										flex={1}
										h="6px"
										bg="whiteAlpha.300"
										borderRadius="full"
										cursor="pointer"
										onClick={handleSeek}
										position="relative"
									>
										<Box
											h="100%"
											bg="white"
											borderRadius="full"
											ref={progressBarRef}
											w="0%"
										/>
									</Box>
									<Text ref={timeDisplayRef} color="white" fontSize="xs" flexShrink={0} whiteSpace="nowrap" display={{ base: 'none', sm: 'block' }}>
										0:00 / 0:00
									</Text>
									<Box
										as="button"
										onClick={() => setIsExpanded(true)}
										color="white"
										cursor="pointer"
										flexShrink={0}
										_hover={{ opacity: 0.8 }}
									>
										<Box as={FaExpand} fontSize="12px" />
									</Box>
								</Flex>
							)}
						</>
					) : (
						<Flex w="100%" h="100%" align="center" justify="center" bg="gray.100" borderRadius="lg">
							<FusionLoading isLoading />
						</Flex>
					)}
					{callToAction && (
						<Flex
							position="absolute"
							bottom={5}
							width="100%"
							justifyContent="center"
							zIndex={3}
						>
							<Button
								size="sm"
								cursor="default"
								bg="gray.100"
								px={3}
								_hover={{ bg: 'gray.100' }}
								fontWeight={500}
								leftIcon={<IoMdLink />}
							>
								{formatCallToAction(callToAction)}
							</Button>
						</Flex>
					)}
				</Box>
			)}
		</CreativeCard>
		{isExpanded && videoTemplate && (
			<VideoExpandModal
				isOpen={isExpanded}
				onClose={() => setIsExpanded(false)}
				template={videoTemplate}
				creativeId={creative.id}
			/>
		)}
		</>
	);
};

export default VideoStoryCreative;
